<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import RemoteSigningLayout from '@/Layouts/RemoteSigningLayout.vue'
import UiButton from '@/Components/UI/UiButton.vue'

defineOptions({ layout: RemoteSigningLayout })

const props = defineProps({
  token:      { type: String, required: true },
  expires_at: { type: String, default: null },
  brand:      { type: Object, default: () => ({}) },
  customer:   { type: Object, default: () => ({}) },
  rental:     { type: Object, default: () => ({}) },
  pdf_url:    { type: String, required: true },
  submit_url: { type: String, required: true },
})

const { t } = useI18n()

const canvasRef    = ref(null)
const hasStrokes   = ref(false)
const submitting   = ref(false)
const errorMsg     = ref(null)
let drawing = false

const expiresLabel = computed(() => {
  if (!props.expires_at) return ''
  return new Date(props.expires_at).toLocaleString()
})

const fmtRange = (raw) => raw ? new Date(raw).toLocaleDateString() : '—'

const sizeCanvas = () => {
  const c = canvasRef.value
  if (!c) return
  const r = c.getBoundingClientRect()
  const dpr = window.devicePixelRatio || 1
  c.width  = r.width * dpr
  c.height = r.height * dpr
  const ctx = c.getContext('2d')
  ctx.scale(dpr, dpr)
  ctx.lineWidth = 2.5
  ctx.lineCap   = 'round'
  ctx.strokeStyle = '#111827'
}

const point = (e) => {
  const rect = canvasRef.value.getBoundingClientRect()
  const src = e.touches ? e.touches[0] : e
  return { x: src.clientX - rect.left, y: src.clientY - rect.top }
}

const start = (e) => {
  e.preventDefault()
  drawing = true
  const p = point(e)
  const ctx = canvasRef.value.getContext('2d')
  ctx.beginPath()
  ctx.moveTo(p.x, p.y)
}

const move = (e) => {
  if (!drawing) return
  e.preventDefault()
  const p = point(e)
  const ctx = canvasRef.value.getContext('2d')
  ctx.lineTo(p.x, p.y)
  ctx.stroke()
  hasStrokes.value = true
}

const end = (e) => {
  if (!drawing) return
  e.preventDefault()
  drawing = false
}

const clearCanvas = () => {
  const c = canvasRef.value
  c.getContext('2d').clearRect(0, 0, c.width, c.height)
  hasStrokes.value = false
}

const submit = async () => {
  if (!hasStrokes.value || submitting.value) return
  submitting.value = true
  errorMsg.value = null
  try {
    const dataUrl = canvasRef.value.toDataURL('image/png')
    const res = await fetch(props.submit_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ signature: dataUrl }),
    })
    if (res.ok) {
      window.location.replace('/sign/done')
      return
    }
    errorMsg.value = `HTTP ${res.status}`
  } catch (e) {
    errorMsg.value = e.message || 'Network error'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  sizeCanvas()
  window.addEventListener('resize', sizeCanvas)
})
</script>

<template>
  <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-4 mb-4">
    <h1 class="font-display text-lg text-[color:var(--ink)]">
      {{ t('remoteSigning.greeting', { name: customer.first_name || '' }) }}
    </h1>
    <p class="text-sm text-[color:var(--inkSoft)] mt-1">
      {{ t('remoteSigning.intro') }}
    </p>
  </section>

  <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-4 mb-4">
    <dl class="grid grid-cols-2 gap-y-2 text-sm">
      <dt class="text-[color:var(--muted)]">{{ t('remoteSigning.vehicle') }}</dt>
      <dd class="fw-value text-[color:var(--ink)]">{{ rental.vehicle_label }}</dd>

      <dt class="text-[color:var(--muted)]">{{ t('remoteSigning.registration') }}</dt>
      <dd class="fw-value tabular-nums text-[color:var(--ink)]">{{ rental.vehicle_reg }}</dd>

      <dt class="text-[color:var(--muted)]">{{ t('remoteSigning.from') }}</dt>
      <dd class="fw-value text-[color:var(--ink)]">{{ fmtRange(rental.start_at) }}</dd>

      <dt class="text-[color:var(--muted)]">{{ t('remoteSigning.to') }}</dt>
      <dd class="fw-value text-[color:var(--ink)]">{{ fmtRange(rental.expected_end_at) }}</dd>

      <dt class="text-[color:var(--muted)]">{{ t('remoteSigning.dailyRate') }}</dt>
      <dd class="fw-value tabular-nums text-[color:var(--ink)]">{{ rental.daily_rate }}</dd>

      <dt class="text-[color:var(--muted)]">{{ t('remoteSigning.total') }}</dt>
      <dd class="fw-value tabular-nums text-[color:var(--ink)]">{{ rental.total }}</dd>

      <dt class="text-[color:var(--muted)]">{{ t('remoteSigning.deposit') }}</dt>
      <dd class="fw-value tabular-nums text-[color:var(--ink)]">{{ rental.deposit }}</dd>
    </dl>
  </section>

  <section class="mb-4">
    <a :href="pdf_url" target="_blank" rel="noopener"
       class="inline-flex items-center gap-2 text-sm text-[color:var(--accent)] underline">
      {{ t('remoteSigning.viewContract') }} →
    </a>
  </section>

  <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-4 mb-4">
    <p class="text-xs text-[color:var(--muted)] mb-2">
      {{ t('remoteSigning.signaturePrompt') }}
    </p>
    <canvas ref="canvasRef"
            class="block w-full bg-white border border-[color:var(--line)] rounded-[var(--radius-md)]"
            style="height: 180px; touch-action: none;"
            @mousedown="start" @mousemove="move" @mouseup="end" @mouseleave="end"
            @touchstart="start" @touchmove="move" @touchend="end" @touchcancel="end" />
    <div class="flex items-center justify-between mt-2 gap-2">
      <button type="button"
              class="text-xs text-[color:var(--muted)] underline"
              @click="clearCanvas">
        {{ t('remoteSigning.clear') }}
      </button>
      <span v-if="expiresLabel" class="text-xs text-[color:var(--muted)]">
        {{ t('remoteSigning.expires', { when: expiresLabel }) }}
      </span>
    </div>
  </section>

  <UiButton variant="primary" size="lg" class="w-full"
            :disabled="!hasStrokes || submitting"
            :loading="submitting"
            @click="submit">
    {{ t('remoteSigning.submit') }}
  </UiButton>

  <p v-if="errorMsg" class="mt-3 text-sm text-[color:var(--danger)] text-center">{{ errorMsg }}</p>
</template>
